// FILE: src/services/testimonialsService.js
//
// Learner testimonials — public read + admin CRUD (migration 078).
//
// Public path:  getPublicTestimonials() calls the SECURITY DEFINER RPC
//   `public_testimonials`, which returns ONLY public-safe columns of rows
//   that are BOTH is_active AND consent_confirmed, pre-sorted
//   (featured DESC, display_order ASC, updated_at DESC). Follows the
//   foundingTesterService pattern: never throws, returns [] on any failure
//   so the home page simply hides the section.
//
// Admin path: direct table access under the admin-only RLS policies
//   (testimonials_admin_select / testimonials_admin_write) — the database
//   is the authority; AdminGate is defense-in-depth only. Admin functions
//   THROW on failure so the manager UI can surface the error.

import { supabase } from './supabaseClient';
import { UploadValidationError } from './imageUploadService';
import { getAllExams } from '../config/exams';

// ── Constants (mirror the DB CHECK constraints in migration 078) ────────────

export const TESTIMONIAL_IMAGES_BUCKET = 'testimonial-images';
export const IMAGE_MAX_BYTES = 2 * 1024 * 1024; // 2 MB — mirrors the bucket cap
export const IMAGE_ALLOWED_MIME_TYPES = Object.freeze([
  'image/png', 'image/jpeg', 'image/webp',
]);
export const LIMITS = Object.freeze({
  displayName: 80,
  professionalTitle: 120,
  organization: 120,
  testimonialText: 1200,
  attributionLabel: 60,
});

const MIME_TO_EXT = Object.freeze({
  'image/png': 'png', 'image/jpeg': 'jpg', 'image/webp': 'webp',
});

// ── Public read ──────────────────────────────────────────────────────────────

/**
 * Active + consent-confirmed testimonials for the public home page,
 * pre-sorted by the RPC. Returns [] on any failure (section hides itself).
 */
export async function getPublicTestimonials() {
  try {
    const { data, error } = await supabase.rpc('public_testimonials');
    if (error) {
      console.warn('[testimonialsService] public_testimonials failed:', error.message);
      return [];
    }
    return Array.isArray(data) ? data : [];
  } catch (e) {
    console.warn('[testimonialsService] public_testimonials threw:', e);
    return [];
  }
}

// ── Validation (shared by the admin form and its tests) ─────────────────────

const isHttpsUrl = (value) => {
  try {
    return new URL(value).protocol === 'https:';
  } catch {
    return false;
  }
};

/**
 * Validates admin-form fields against the same rules the DB enforces.
 * Returns { ok, errors } where errors maps field → message.
 */
export function validateTestimonialFields(fields) {
  const errors = {};
  const name = (fields.display_name || '').trim();
  const text = (fields.testimonial_text || '').trim();
  if (!name) errors.display_name = 'Display name is required.';
  else if (name.length > LIMITS.displayName) errors.display_name = `Max ${LIMITS.displayName} characters.`;
  if (!text) errors.testimonial_text = 'Testimonial text is required.';
  else if (text.length > LIMITS.testimonialText) errors.testimonial_text = `Max ${LIMITS.testimonialText} characters.`;
  if ((fields.professional_title || '').length > LIMITS.professionalTitle) errors.professional_title = `Max ${LIMITS.professionalTitle} characters.`;
  if ((fields.organization || '').length > LIMITS.organization) errors.organization = `Max ${LIMITS.organization} characters.`;
  if ((fields.attribution_label || '').length > LIMITS.attributionLabel) errors.attribution_label = `Max ${LIMITS.attributionLabel} characters.`;

  const validCerts = getAllExams().map((e) => e.examId);
  if (!fields.exam_id || !validCerts.includes(fields.exam_id)) {
    errors.exam_id = 'Select a supported certification.';
  }
  for (const key of ['linkedin_url', 'verification_url', 'profile_image_url']) {
    if (fields[key] && !isHttpsUrl(fields[key])) {
      errors[key] = 'Must be a valid https:// URL.';
    }
  }
  return { ok: Object.keys(errors).length === 0, errors };
}

/** True when a row meets every public-display requirement. */
export function isPubliclyEligible(row) {
  return Boolean(
    row && row.is_active && row.consent_confirmed &&
    validateTestimonialFields(row).ok
  );
}

// ── Admin CRUD (RLS-gated; throws on failure) ───────────────────────────────

const WRITABLE_FIELDS = [
  'display_name', 'professional_title', 'organization', 'testimonial_text',
  'exam_id', 'certification_date', 'profile_image_url', 'linkedin_url',
  'verification_url', 'attribution_label', 'is_featured', 'is_active',
  'display_order', 'consent_confirmed', 'consent_confirmed_at',
];

async function callerEmail() {
  try {
    const { data } = await supabase.auth.getSession();
    return data?.session?.user?.email || null;
  } catch {
    return null;
  }
}

function sanitizePayload(fields) {
  const out = {};
  for (const key of WRITABLE_FIELDS) {
    if (key in fields) out[key] = fields[key] === '' ? null : fields[key];
  }
  // Consent audit trail: stamp/clear the timestamp with the flag.
  if (out.consent_confirmed && !out.consent_confirmed_at) {
    out.consent_confirmed_at = new Date().toISOString();
  }
  if (out.consent_confirmed === false) out.consent_confirmed_at = null;
  return out;
}

/** All testimonials for the admin list (admin-only via RLS). */
export async function listTestimonials() {
  const { data, error } = await supabase
    .from('testimonials')
    .select('*')
    .order('is_featured', { ascending: false })
    .order('display_order', { ascending: true })
    .order('updated_at', { ascending: false });
  if (error) throw new Error(`listTestimonials failed: ${error.message}`);
  return Array.isArray(data) ? data : [];
}

export async function createTestimonial(fields) {
  const payload = sanitizePayload(fields);
  payload.created_by = await callerEmail();
  payload.updated_by = payload.created_by;
  const { data, error } = await supabase
    .from('testimonials')
    .insert(payload)
    .select()
    .single();
  if (error) throw new Error(`createTestimonial failed: ${error.message}`);
  return data;
}

export async function updateTestimonial(id, fields) {
  const payload = sanitizePayload(fields);
  payload.updated_by = await callerEmail();
  const { data, error } = await supabase
    .from('testimonials')
    .update(payload)
    .eq('id', id)
    .select()
    .single();
  if (error) throw new Error(`updateTestimonial failed: ${error.message}`);
  return data;
}

export async function deleteTestimonial(id) {
  const { error } = await supabase
    .from('testimonials')
    .delete()
    .eq('id', id);
  if (error) throw new Error(`deleteTestimonial failed: ${error.message}`);
}

/** Persists a display_order swap between two rows (up/down reordering). */
export async function swapTestimonialOrder(a, b) {
  await updateTestimonial(a.id, { display_order: b.display_order });
  await updateTestimonial(b.id, { display_order: a.display_order });
}

// ── Profile image upload ─────────────────────────────────────────────────────

/**
 * Uploads a profile image to the testimonial-images bucket.
 * Client-side validation mirrors the bucket constraints (2 MB, png/jpeg/webp)
 * for fast, friendly feedback; the bucket stays authoritative.
 * @returns {Promise<{ publicUrl: string, path: string }>}
 */
export async function uploadTestimonialImage(file) {
  if (!file) throw new UploadValidationError('No file selected.');
  if (!IMAGE_ALLOWED_MIME_TYPES.includes(file.type)) {
    throw new UploadValidationError(
      `Unsupported file type "${file.type || 'unknown'}". Allowed: PNG, JPG, WebP.`,
    );
  }
  if (file.size > IMAGE_MAX_BYTES) {
    const mb = (file.size / 1024 / 1024).toFixed(1);
    throw new UploadValidationError(`File is too large (${mb} MB). Maximum is 2 MB.`);
  }
  const ext = MIME_TO_EXT[file.type] || 'bin';
  const path = `avatars/${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`;
  const { error: uploadError } = await supabase.storage
    .from(TESTIMONIAL_IMAGES_BUCKET)
    .upload(path, file, { cacheControl: '31536000', upsert: false, contentType: file.type });
  if (uploadError) {
    throw new Error(`Upload failed: ${uploadError.message || 'unknown error'}`);
  }
  const { data } = supabase.storage.from(TESTIMONIAL_IMAGES_BUCKET).getPublicUrl(path);
  if (!data || !data.publicUrl) {
    throw new Error('Upload succeeded but the public URL could not be resolved.');
  }
  return { publicUrl: data.publicUrl, path };
}
